import React, { useState, useEffect } from 'react';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { Box, Typography, Alert, CircularProgress, Button } from '@mui/material';

interface OrderItem {
  name: string;
  price: number;
  quantity: number;
  productId: string;
}

interface PayPalCheckoutProps {
  orderItems: OrderItem[];
  totalAmount: number;
  onPaymentSuccess: (details: any) => void;
  onPaymentError: (error: any) => void;
  onPaymentCancel: () => void;
}

const PayPalCheckout: React.FC<PayPalCheckoutProps> = ({
  orderItems,
  totalAmount,
  onPaymentSuccess,
  onPaymentError,
  onPaymentCancel
}) => {
  const [{ isPending, isRejected, isInitial, isResolved }] = usePayPalScriptReducer();
  const [scriptError, setScriptError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>({});

  useEffect(() => {
    console.log('PayPal Script Status:', { isPending, isRejected, isInitial, isResolved });
    console.log('Order Items:', orderItems);
    console.log('Total Amount:', totalAmount);
    
    setDebugInfo({
      isPending,
      isRejected,
      isInitial,
      isResolved,
      totalAmount,
      itemCount: orderItems.length
    });
    
    if (isRejected) {
      setScriptError('PayPal script failed to load. Please refresh the page and try again.');
    }
  }, [isPending, isRejected, isInitial, isResolved, orderItems, totalAmount]);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const apiFetch = async (path: string, body?: any) => {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
      },
      body: JSON.stringify(body || {})
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || data.message || `Request failed (${response.status})`);
    }
    return data;
  };

  // Option A: the server creates the order (DB-authoritative pricing) and the
  // PayPal v2 order. The SDK only receives the PayPal order id — the client
  // never sends amounts or payment status.
  const createOrder = async () => {
    console.log('Creating server order for items:', orderItems);

    const orderResult = await apiFetch('/orders', {
      items: orderItems.map(item => ({
        productId: item.productId,
        quantity: item.quantity
      })),
      paymentMethod: 'paypal'
    });

    const orderId = orderResult.data?.orderId || orderResult.orderId;
    console.log('Server order created:', orderId);

    const paypalResult = await apiFetch('/payments/paypal/create-order', { orderId });
    console.log('PayPal order created:', paypalResult.paypalOrderId);

    return paypalResult.paypalOrderId;
  };

  // Capture happens server-side; downloads are issued by the idempotent
  // completion routine and returned in the capture response.
  const onApprove = async (data: any) => {
    try {
      console.log('PayPal order approved:', data);

      const result = await apiFetch(`/payments/paypal/capture/${data.orderID}`);
      console.log('Server capture result:', result);

      onPaymentSuccess({
        id: data.orderID,
        status: 'COMPLETED',
        orderId: result.orderId,
        transactionId: result.transactionId,
        downloads: result.downloads || []
      });
    } catch (error) {
      console.error('Error capturing PayPal payment:', error);
      onPaymentError(error);
    }
  };

  const onError = (err: any) => {
    console.error('PayPal error:', err);
    onPaymentError(err);
  };

  const onCancel = () => {
    console.log('PayPal payment cancelled');
    onPaymentCancel();
  };

  // For development testing - simulate a successful payment. Downloads are
  // only issued by the server capture flow, so the mock carries none.
  const handleTestPayment = async () => {
    console.log('Simulating test payment (no downloads issued — server capture only)');

    onPaymentSuccess({
      id: 'mock-payment-id',
      status: 'COMPLETED',
      orderId: 'mock-order',
      transactionId: 'mock-capture',
      downloads: []
    });
  };

  if (isPending || isInitial) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1">
          Loading PayPal...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we connect to PayPal
        </Typography>
      </Box>
    );
  }

  if (isRejected || scriptError) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          PayPal script failed to load. Please use the test payment button below.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You can test the complete payment flow including download creation using the button below.
        </Typography>
        
        {/* Test Payment Button */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleTestPayment}
            sx={{ 
              mb: 2,
              backgroundColor: '#28a745',
              '&:hover': { backgroundColor: '#218838' },
              fontSize: '1.1rem',
              padding: '12px 24px'
            }}
            startIcon={
              <Box component="span" sx={{ 
                width: 24, 
                height: 24, 
                backgroundColor: 'white', 
                borderRadius: '50%',
                display: 'inline-block',
                mr: 1
              }} />
            }
          >
            🧪 Test Payment & Create Downloads
          </Button>
        </Box>
        
        {/* Debug info for development */}
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" component="div">
              Debug Info: {JSON.stringify(debugInfo, null, 2)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
        Complete Your Purchase
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total: ${totalAmount.toFixed(2)}
        </Typography>
      </Box>

      <PayPalButtons
        createOrder={createOrder}
        onApprove={onApprove}
        onError={onError}
        onCancel={onCancel}
        style={{
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'pay'
        }}
      />
      
      {/* Test button for development */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleTestPayment}
          >
            Test Payment with Downloads
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PayPalCheckout; 